"use memo"

import { ReactNode } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
  SetupNotificationsLayout,
  HubEventHandler,
} from 'layout/SetupNotificationsLayout'
import { HUB_NAMES } from 'constants/signalrConstants'
import {
  NotificationType,
  CampaignUpdatedEvent,
  RexListingCreatedEvent,
  TaskUpdatedNotification,
  PdfProcessedEvent,
  TaskCreatedNotification,
} from 'types/pushNotifications'
import { emitTaskUpdate } from '../hooks/useAutopilotTaskUpdates'
import {
  handleCampaignUpdated,
  handleRexListingCreated,
} from './appNotificationsHandlers'
import { createCampaignDetailsQueryKey } from 'hooks/useSelectedCampaign'
import { emitPdfProcessedEvent } from 'hooks/usePdfProcessedEvent'
import { emitTaskCreatedEvent } from 'hooks/useTaskCreatedEvent'
import {
  useInvalidateUseGetArchivedCampaignsByAdmin,
  useInvalidateUseGetCampaignsForAgency,
} from '@urbanx/agx.campaigns.client'
import { useInvalidateUseGetListingByCampaignId } from '@urbanx/agx.rex.client'

interface SignalrCommsProps {
  children: ReactNode
}

const handleAppNotificationsConnected = () =>
  console.log('AppNotifications hub connected')

const handleAutopilotTasksConnected = () =>
  console.log('AutopilotTasks hub connected')

const SignalrComms = ({ children }: SignalrCommsProps) => {
  const queryClient = useQueryClient()
  const invalidateCampaignListing = useInvalidateUseGetListingByCampaignId()
  const invalidateCampaigns = useInvalidateUseGetCampaignsForAgency()
  const invalidateArchivedCampaigns =
    useInvalidateUseGetArchivedCampaignsByAdmin()
  const invalidateSelectedCampaign = async (campaignId: string) => {
    await queryClient.invalidateQueries({
      queryKey: createCampaignDetailsQueryKey(campaignId),
    })
  }
  const invalidateCampaignScopedQueries = async (campaignId: string) => {
    await queryClient.invalidateQueries({
      predicate: (query) =>
        Array.isArray(query.queryKey) && query.queryKey.includes(campaignId),
    })
  }

  const appNotificationHandlers: HubEventHandler[] = [
    {
      eventType: NotificationType.CampaignUpdatedEvent,
      handler: (event: CampaignUpdatedEvent) =>
        handleCampaignUpdated(
          event,
          invalidateCampaignScopedQueries,
          invalidateSelectedCampaign,
          [invalidateCampaigns, invalidateArchivedCampaigns]
        ),
    },
    {
      eventType: NotificationType.PdfProcessedEvent,
      handler: (event: PdfProcessedEvent) => emitPdfProcessedEvent(event),
    },
    {
      eventType: NotificationType.RexListingCreatedEvent,
      handler: (event: RexListingCreatedEvent) =>
        handleRexListingCreated(event, invalidateCampaignListing),
    },
  ]

  const autopilotTaskHandlers: HubEventHandler[] = [
    {
      eventType: NotificationType.TaskUpdatedNotification,
      handler: (event: TaskUpdatedNotification) => emitTaskUpdate(event),
    },
    {
      eventType: NotificationType.TaskCreatedNotification,
      handler: (event: TaskCreatedNotification) => emitTaskCreatedEvent(event),
    },
  ]
  return (
    <SetupNotificationsLayout
      hubName={HUB_NAMES.APP_NOTIFICATIONS}
      eventHandlers={appNotificationHandlers}
      onConnected={handleAppNotificationsConnected}
    >
      <SetupNotificationsLayout
        hubName={HUB_NAMES.AUTOPILOT_TASKS}
        eventHandlers={autopilotTaskHandlers}
        onConnected={handleAutopilotTasksConnected}
      >
        {children}
      </SetupNotificationsLayout>
    </SetupNotificationsLayout>
  )
}

export default SignalrComms
